import type { z } from 'zod';

/**
 * A portal read answered a shape its schema does not describe: the frontend is wrong about the
 * shape, or the API changed it without telling us. The portal-wide twin of data analysis's
 * `ContractViolationError` (`features/data-analysis/api/http/parse.ts`), and named like it on
 * purpose: the server-state instrumentation and the e2e app-error guard find a violation by
 * `name`, so this one is reported wherever that one is.
 *
 * Its message carries issue paths and zod codes only. A zod message can echo the value it
 * refused, and these bodies hold names, emails and phone numbers.
 */
export class ApiContractViolationError extends Error {
  readonly endpoint: string;
  readonly issues: z.ZodIssue[];

  constructor(endpoint: string, error: z.ZodError) {
    super(`The API returned an unexpected shape from ${endpoint}: ${describeIssues(error.issues)}`);
    this.name = 'ContractViolationError';
    this.endpoint = endpoint;
    this.issues = error.issues;
  }
}

/** How many issues a message names before it only counts the rest. */
const LISTED_ISSUES = 10;

/** `path: code` per issue, the first few: never a value. */
function describeIssues(issues: z.ZodIssue[]): string {
  const listed = issues
    .slice(0, LISTED_ISSUES)
    .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.code}`)
    .join('; ');
  return issues.length > LISTED_ISSUES
    ? `${listed} (+${issues.length - LISTED_ISSUES} more)`
    : listed;
}

/** The endpoints a production build has already warned about: one line each per page load. */
const warned = new Set<string>();

/**
 * Checks a portal read against the schema of what the frontend reads off it, and returns the
 * body exactly as it came: the schema validates, it never reshapes, so a screen sees the same
 * object it saw before the check existed.
 *
 * - Outside a production build (development, Jest, the pre-push smoke's development build) a
 *   violation throws `ApiContractViolationError`, as every data-analysis read does.
 * - In a production build, which includes dev.aivo.ch, it warns once per endpoint, with the
 *   endpoint and the failing paths only, and passes the body on. Why it does not throw there:
 *   DATA_FLOW.md §1, *Response checks on the reads every signed-in page waits on*.
 *
 * `endpoint` names the route, not the request (`GET /audit/:id`), so the warning is once per
 * route rather than once per audit.
 */
export function checkResponse<T>(schema: z.ZodTypeAny, data: T, endpoint: string): T {
  const result = schema.safeParse(data);
  if (result.success) return data;
  if (process.env.NODE_ENV !== 'production') {
    throw new ApiContractViolationError(endpoint, result.error);
  }
  if (!warned.has(endpoint)) {
    warned.add(endpoint);
    console.warn(
      `[api contract] ${endpoint} answered an unexpected shape: ${describeIssues(result.error.issues)}`,
    );
  }
  return data;
}
